<script setup lang="ts">
import { computed } from 'vue';

import { CustomTraysProps } from '@/views/CustomTrays.vue';

const props = defineProps<{
  activeFilters: string[];
  activeKeyword?: string;
  activeSort: 'popularity' | 'price';
  ingredients: CustomTraysProps['ingredientCatalog'];
  showPrice: boolean;
}>();

const activeIngredientId = defineModel<string>('activeIngredientId');

const ingredientCatalog = computed(() =>
  props.ingredients
    .map((ingredient) => {
      let show = props.activeFilters.every((val) => ingredient.filters.includes(val));

      const { activeKeyword } = props;
      if (activeKeyword?.length) {
        const nameContainsKeyword = ingredient.name.toLowerCase().indexOf(activeKeyword) !== -1;
        const filtersContainKeyword =
          ingredient.filters.filter((val) => val.toLowerCase().indexOf(activeKeyword) !== -1)
            .length > 0;
        show = show && (nameContainsKeyword || filtersContainKeyword);
      }
      return {
        ...ingredient,
        active: activeIngredientId.value === ingredient.id,
        show,
      };
    })
    .sort((ingredientL, ingredientR) => {
      let diff = 0;

      if (props.activeSort === 'popularity') {
        diff = ingredientR.popularity - ingredientL.popularity;
      } else if (props.activeSort === 'price') {
        diff =
          ingredientR.priceRange.length - ingredientL.priceRange.length ||
          ingredientR.priceLo - ingredientL.priceLo;
      }

      if (Math.abs(diff) > 0.0000001) {
        return diff;
      }

      return ingredientL.name.toLowerCase() > ingredientR.name.toLowerCase() ? 1 : -1;
    }),
);

const startDrag = (e: DragEvent, ingredient: CustomTraysProps['ingredientCatalog'][number]) => {
  if (!e.dataTransfer) return;
  // eslint-disable-next-line no-param-reassign
  e.dataTransfer.dropEffect = 'move';
  // eslint-disable-next-line no-param-reassign
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('ingredientId', ingredient.id);
};
</script>

<template>
  <div class="flex-row ingredient-catalog wrapped pb-15">
    <div
      v-for="ingredient in ingredientCatalog"
      v-show="ingredient.show"
      :key="ingredient.id"
      class="w-1/2 ws-sm:w-1/3 ws-lg:w-1/4 ingredient-catalog-tile"
      :class="{ active: ingredient.active }"
      :data-test="ingredient.name"
      @click="activeIngredientId = ingredient.id"
      draggable="true"
      @dragstart="startDrag($event, ingredient)"
    >
      <div class="relative tile-contents" data-test="relevant-product">
        <div class="photo-container vertical-align-middle prop-3x2">
          <img
            :src="ingredient.assetPath"
            :alt="`${ingredient.name} image`"
            :aria-label="`${ingredient.name} image`"
            class="photo"
            loading="lazy"
          />
        </div>
        <h4>{{ ingredient.name }}</h4>
        <div v-if="showPrice" class="price-range" data-test="price-range">
          {{ ingredient.priceRange }}
        </div>
      </div>
    </div>
  </div>
</template>
